import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SUPPORTED_LANGS, LANGUAGE_LABELS, getLocalizedUrl, getLanguageFromPath } from '../../_i18n/urlHelper';

const LANGUAGE_FLAGS = {
  en: '🇺🇸',
  pt: '🇧🇷',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  it: '🇮🇹',
  nl: '🇳🇱',
  ru: '🇷🇺'
};

const Header = (props) => {
  const location = props.location || (typeof window !== 'undefined' ? window.location : { pathname: '/' });
  const pathLang = getLanguageFromPath(location.pathname).lang;
  const intlLang = props.intl && props.intl.locale ? props.intl.locale.split('-')[0].toLowerCase() : null;
  const activeLang = (props.currentLang && props.currentLang !== 'en')
    ? props.currentLang
    : (pathLang && pathLang !== 'en')
      ? pathLang
      : (intlLang && SUPPORTED_LANGS.includes(intlLang))
        ? intlLang
        : 'en';

  const langPrefix = activeLang && activeLang !== 'en' ? `/${activeLang}` : '';
  const homeUrl = langPrefix || '/';

  const [isNavOpen, setIsNavOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const navRef = useRef(null);

  // Ativa a renderização de menus pesados após primeira interação do usuário (touch/mouse/scroll/click)
  // Isso remove dezenas de links repetidos do HTML inicial do SSR para os robôs do Google
  useEffect(() => {
    const handleInteraction = () => {
      setHasInteracted(true);
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };

    window.addEventListener('mousemove', handleInteraction, { passive: true, once: true });
    window.addEventListener('touchstart', handleInteraction, { passive: true, once: true });
    window.addEventListener('scroll', handleInteraction, { passive: true, once: true });
    window.addEventListener('keydown', handleInteraction, { passive: true, once: true });

    return () => {
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };
  }, []);

  // Fecha os dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const toggleDropdown = (name, e) => {
    if (e) e.preventDefault();
    setHasInteracted(true);
    setActiveDropdown((prev) => (prev === name ? null : name));
  };

  const closeMenu = () => {
    setActiveDropdown(null);
    setIsNavOpen(false);
  };

  return (
    <header ref={navRef} style={{ position: 'relative', zIndex: 9999999 }}>
      <nav className="navbar navbar-expand-lg navbar-dark fixed-top bg-dark shadow-sm" style={{ zIndex: 9999999 }}>
        <a className="navbar-brand" href={homeUrl} onClick={closeMenu}>
          <img src="/logo32.png" alt="stopwatch-timer.net" className="logo mr-2" width="32" height="32" />
          Stopwatch-Timer
        </a>
        <button
          className="navbar-toggler"
          type="button"
          onClick={() => setIsNavOpen(!isNavOpen)}
          aria-controls="navbarCollapse"
          aria-expanded={isNavOpen}
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className={`collapse navbar-collapse ${isNavOpen ? 'show' : ''}`} id="navbarCollapse">
          <ul className="navbar-nav ml-auto align-items-lg-center">
            <li className="nav-item">
              <a className="nav-link" href={homeUrl} title="Online Stopwatch" onClick={closeMenu}>
                ⏱️ <FormattedMessage id="header.nav.stopwatch" defaultMessage="Stopwatch" />
              </a>
            </li>
            <li className="nav-item">
              <a className="nav-link" href={`/countdown-timer${langPrefix}`} title="Online Countdown Timer" onClick={closeMenu}>
                ⏳ <FormattedMessage id="header.nav.countdown" defaultMessage="Countdown Timer" />
              </a>
            </li>
            <li className="nav-item">
              <a className="nav-link" href={`/pomodoro-timer${langPrefix}`} title="Online Pomodoro Timer" onClick={closeMenu}>
                🍅 <FormattedMessage id="header.nav.pomodoro" defaultMessage="Pomodoro Timer" />
              </a>
            </li>

            {/* Exam Prep Timers Dropdown */}
            <li className={`nav-item dropdown ${activeDropdown === 'exam' ? 'show' : ''}`}>
              <a
                className="nav-link dropdown-toggle text-success font-weight-bold"
                href="#exam-timers"
                id="examDropdown"
                role="button"
                onClick={(e) => toggleDropdown('exam', e)}
                aria-haspopup="true"
                aria-expanded={activeDropdown === 'exam'}
              >
                🎯 <FormattedMessage id="header.exam.title" defaultMessage="Exam Prep" /> <span className="badge badge-success text-white ml-1">NEW</span>
              </a>
              {hasInteracted && (
                <div className={`dropdown-menu bg-dark ${activeDropdown === 'exam' ? 'show' : ''}`} aria-labelledby="examDropdown" style={{ maxHeight: '85vh', overflowY: 'auto', zIndex: 10000000 }}>
                  <a className="dropdown-item text-white font-weight-bold" href={`/exam-timer${langPrefix}`} onClick={closeMenu}>
                    🎓 <FormattedMessage id="header.exam.all" defaultMessage="All Exam Simulators" />
                  </a>

                  {/* 🇺🇸 Estados Unidos */}
                  <div className="dropdown-divider border-secondary"></div>
                  <h6 className="dropdown-header text-info font-weight-bold text-uppercase d-flex align-items-center" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    <span className="mr-1">🇺🇸</span> <FormattedMessage id="header.exam.us" defaultMessage="United States" />
                  </h6>
                  <a className="dropdown-item text-white pl-4" href={`/sat-timer${langPrefix}`} onClick={closeMenu}>
                    📘 <FormattedMessage id="header.exam.sat" defaultMessage="Digital SAT® Timer" />
                  </a>
                  <a className="dropdown-item text-white pl-4" href={`/act-timer${langPrefix}`} onClick={closeMenu}>
                    📗 <FormattedMessage id="header.exam.act" defaultMessage="ACT® Exam Timer" />
                  </a>
                  <a className="dropdown-item text-white pl-4" href={`/lsat-timer${langPrefix}`} onClick={closeMenu}>
                    ⚖️ <FormattedMessage id="header.exam.lsat" defaultMessage="LSAT® Section Timer" />
                  </a>
                  <a className="dropdown-item text-white pl-4" href={`/mcat-timer${langPrefix}`} onClick={closeMenu}>
                    🩺 <FormattedMessage id="header.exam.mcat" defaultMessage="MCAT® Full Length" />
                  </a>
                  <a className="dropdown-item text-white pl-4" href={`/gmat-timer${langPrefix}`} onClick={closeMenu}>
                    📊 <FormattedMessage id="header.exam.gmat" defaultMessage="GMAT™ Focus Timer" />
                  </a>
                  <a className="dropdown-item text-white pl-4" href={`/gre-timer${langPrefix}`} onClick={closeMenu}>
                    📈 <FormattedMessage id="header.exam.gre" defaultMessage="GRE® Test Timer" />
                  </a>
                  <a className="dropdown-item text-white pl-4" href={`/bar-exam-timer${langPrefix}`} onClick={closeMenu}>
                    🏛️ <FormattedMessage id="header.exam.bar" defaultMessage="MBE Bar Exam" />
                  </a>

                  {/* 🇧🇷 Brasil */}
                  <div className="dropdown-divider border-secondary"></div>
                  <h6 className="dropdown-header text-warning font-weight-bold text-uppercase d-flex align-items-center" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    <span className="mr-1">🇧🇷</span> <FormattedMessage id="header.exam.br" defaultMessage="Brasil" />
                  </h6>
                  <a className="dropdown-item text-white pl-4" href={`/enem-timer${langPrefix}`} onClick={closeMenu}>
                    🇧🇷 <FormattedMessage id="header.exam.enem" defaultMessage="ENEM Simulado" />
                  </a>
                  <a className="dropdown-item text-white pl-4" href={`/concursos-timer${langPrefix}`} onClick={closeMenu}>
                    🏛️ <FormattedMessage id="header.exam.concursos" defaultMessage="Concursos Públicos" />
                  </a>
                  <a className="dropdown-item text-white pl-4" href={`/concursos-timer${langPrefix}`} onClick={closeMenu}>
                    ⚖️ <FormattedMessage id="header.exam.oab" defaultMessage="OAB 1ª Fase" />
                  </a>

                  {/* ☁️ Global / TI */}
                  <div className="dropdown-divider border-secondary"></div>
                  <h6 className="dropdown-header text-success font-weight-bold text-uppercase d-flex align-items-center" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    <span className="mr-1">☁️</span> <FormattedMessage id="header.exam.global" defaultMessage="Global IT" />
                  </h6>
                  <a className="dropdown-item text-white pl-4" href={`/aws-timer${langPrefix}`} onClick={closeMenu}>
                    ☁️ <FormattedMessage id="header.exam.aws" defaultMessage="AWS® Certification" />
                  </a>
                </div>
              )}
            </li>

            {/* Classroom Timers Dropdown */}
            <li className={`nav-item dropdown ${activeDropdown === 'classroom' ? 'show' : ''}`}>
              <a
                className="nav-link dropdown-toggle text-warning font-weight-bold"
                href="#classroom-timers"
                id="classroomDropdown"
                role="button"
                onClick={(e) => toggleDropdown('classroom', e)}
                aria-haspopup="true"
                aria-expanded={activeDropdown === 'classroom'}
              >
                🏫 <FormattedMessage id="header.nav.classroom" defaultMessage="Classroom Timers" /> <span className="badge badge-warning text-dark ml-1">NEW</span>
              </a>
              {hasInteracted && (
                <div className={`dropdown-menu bg-dark ${activeDropdown === 'classroom' ? 'show' : ''}`} aria-labelledby="classroomDropdown" style={{ maxHeight: '85vh', overflowY: 'auto', zIndex: 10000000 }}>
                  <h6 className="dropdown-header text-warning font-weight-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    🏎️ <FormattedMessage id="header.nav.raceTimers" defaultMessage="Race Timers" />
                  </h6>
                  <a className="dropdown-item text-white" href={`/monster-race${langPrefix}`} onClick={closeMenu}>
                    👾 <FormattedMessage id="header.nav.monsterRace" defaultMessage="Monster Race" />
                  </a>
                  <a className="dropdown-item text-white" href={`/rocket-race${langPrefix}`} onClick={closeMenu}>
                    🚀 <FormattedMessage id="header.nav.rocketRace" defaultMessage="Rocket Race" />
                  </a>
                  <a className="dropdown-item text-white" href={`/running-race${langPrefix}`} onClick={closeMenu}>
                    🏃 <FormattedMessage id="header.nav.runningRace" defaultMessage="Running Race" />
                  </a>
                  <a className="dropdown-item text-white" href={`/swimming-race${langPrefix}`} onClick={closeMenu}>
                    🏊 <FormattedMessage id="header.nav.swimmingRace" defaultMessage="Swimming Race" />
                  </a>
                  <a className="dropdown-item text-white" href={`/snail-race${langPrefix}`} onClick={closeMenu}>
                    🐌 <FormattedMessage id="header.nav.snailRace" defaultMessage="Snail Race" /> <span className="badge badge-warning text-dark ml-1">NEW</span>
                  </a>
                  <a className="dropdown-item text-white" href={`/duck-race${langPrefix}`} onClick={closeMenu}>
                    🦆 <FormattedMessage id="header.nav.duckRace" defaultMessage="Duck Race" /> <span className="badge badge-warning text-dark ml-1">NEW</span>
                  </a>
                  <a className="dropdown-item text-white" href={`/horse-race${langPrefix}`} onClick={closeMenu}>
                    🐎 <FormattedMessage id="header.nav.horseRace" defaultMessage="Horse Race" /> <span className="badge badge-warning text-dark ml-1">NEW</span>
                  </a>

                  <div className="dropdown-divider border-secondary"></div>
                  <h6 className="dropdown-header text-success font-weight-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    🎮 <FormattedMessage id="header.nav.games" defaultMessage="Games" />
                  </h6>
                  <a className="dropdown-item text-white font-weight-bold pl-3" href={`/math-bingo${langPrefix}`} onClick={closeMenu}>
                    🎯 <FormattedMessage id="header.nav.mathBingo" defaultMessage="Math Bingo Game" /> <span className="badge badge-warning text-dark ml-1">HOT</span>
                  </a>
                  <a className="dropdown-item text-white font-weight-bold pl-3" href={`/math-wizard${langPrefix}`} onClick={closeMenu}>
                    🧙‍♂️ <FormattedMessage id="header.nav.mathWizard" defaultMessage="Math Wizard RPG" /> <span className="badge badge-warning text-dark ml-1">HOT</span>
                  </a>
                  <a className="dropdown-item text-white font-weight-bold pl-3" href={`/adventure-runner${langPrefix}`} onClick={closeMenu}>
                    🧭 <FormattedMessage id="header.nav.adventureRunner" defaultMessage="Adventure Runner" /> <span className="badge badge-warning text-dark ml-1">HOT</span>
                  </a>
                  <a className="dropdown-item text-white font-weight-bold pl-3" href={`/digital-escape-room${langPrefix}`} onClick={closeMenu}>
                    🔐 <FormattedMessage id="header.nav.escapeRoom" defaultMessage="Digital Escape Room" /> <span className="badge badge-warning text-dark ml-1">NEW</span>
                  </a>
                  <a className="dropdown-item text-white font-weight-bold pl-3" href={`/pacman-timer${langPrefix}`} onClick={closeMenu}>
                    🕹️ <FormattedMessage id="header.nav.pacman" defaultMessage="Pac-Man Classroom Timer" /> <span className="badge badge-warning text-dark ml-1">HOT</span>
                  </a>

                  <div className="dropdown-divider border-secondary"></div>
                  <h6 className="dropdown-header text-info font-weight-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    🔊 <FormattedMessage id="header.nav.noiseFocus" defaultMessage="Noise & Focus" />
                  </h6>
                  <a className="dropdown-item text-white font-weight-bold pl-3" href={`/visual-timer${langPrefix}`} onClick={closeMenu}>
                    🟢 <FormattedMessage id="header.nav.visualTimer" defaultMessage="Visual Classroom Timer" /> <span className="badge badge-warning text-dark ml-1">HOT</span>
                  </a>
                  <a className="dropdown-item text-white font-weight-bold pl-3" href={`/bouncy-balls-noise-meter${langPrefix}`} onClick={closeMenu}>
                    🔴 <FormattedMessage id="header.nav.bouncyBalls" defaultMessage="Bouncy Balls Noise Meter" /> <span className="badge badge-danger text-white ml-1">HOT</span>
                  </a>

                  <div className="dropdown-divider border-secondary"></div>
                  <h6 className="dropdown-header text-warning font-weight-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    💥 <FormattedMessage id="header.nav.funCountdown" defaultMessage="Fun Countdown" />
                  </h6>
                  <a className="dropdown-item text-white" href={`/dynamite-timer${langPrefix}`} onClick={closeMenu}>
                    🧨 <FormattedMessage id="header.nav.dynamite" defaultMessage="Dynamite Timer" />
                  </a>
                  <a className="dropdown-item text-white" href={`/egg-timer${langPrefix}`} onClick={closeMenu}>
                    🍳 <FormattedMessage id="header.nav.egg" defaultMessage="Egg Timer" />
                  </a>
                  <a className="dropdown-item text-white" href={`/candle-timer${langPrefix}`} onClick={closeMenu}>
                    🕯️ <FormattedMessage id="header.nav.candle" defaultMessage="Candle Timer" />
                  </a>
                  <a className="dropdown-item text-white" href={`/bomb-timer${langPrefix}`} onClick={closeMenu}>
                    💣 <FormattedMessage id="header.nav.bomb" defaultMessage="Bomb Timer" />
                  </a>
                  <a className="dropdown-item text-white" href={`/rocket-timer${langPrefix}`} onClick={closeMenu}>
                    🚀 <FormattedMessage id="header.nav.rocket" defaultMessage="Rocket Timer" />
                  </a>
                  <a className="dropdown-item text-white" href={`/cat-timer${langPrefix}`} onClick={closeMenu}>
                    🐱 <FormattedMessage id="header.nav.cat" defaultMessage="Cat Timer" />
                  </a>
                  <a className="dropdown-item text-white" href={`/christmas-timer${langPrefix}`} onClick={closeMenu}>
                    🎄 <FormattedMessage id="header.nav.christmas" defaultMessage="Christmas Timer" />
                  </a>
                  <a className="dropdown-item text-white" href={`/countdown-new-year${langPrefix}`} onClick={closeMenu}>
                    🎆 <FormattedMessage id="header.nav.newYear" defaultMessage="New Year Countdown" />
                  </a>
                </div>
              )}
            </li>

            {/* More / Mais Dropdown (Tools + Games + Blog + Hub) */}
            <li className={`nav-item dropdown ${activeDropdown === 'more' ? 'show' : ''}`}>
              <a
                className="nav-link dropdown-toggle"
                href="#more"
                id="moreDropdown"
                role="button"
                onClick={(e) => toggleDropdown('more', e)}
                aria-haspopup="true"
                aria-expanded={activeDropdown === 'more'}
              >
                🛠️ <FormattedMessage id="header.nav.more" defaultMessage="More" />
              </a>
              {hasInteracted && (
                <div className={`dropdown-menu dropdown-menu-right bg-dark ${activeDropdown === 'more' ? 'show' : ''}`} aria-labelledby="moreDropdown" style={{ zIndex: 10000000 }}>
                  <h6 className="dropdown-header text-info font-weight-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    🛠️ <FormattedMessage id="header.nav.tools" defaultMessage="Tools" />
                  </h6>
                  <a className="dropdown-item text-white" href={`/date-calculator${langPrefix}`} onClick={closeMenu}>
                    📅 <FormattedMessage id="header.nav.dateCalculator" defaultMessage="Date Calculator" />
                  </a>
                  <a className="dropdown-item text-white" href={`/clock${langPrefix}`} onClick={closeMenu}>
                    🕒 <FormattedMessage id="header.nav.clock" defaultMessage="Clock" />
                  </a>

                  <div className="dropdown-divider border-secondary"></div>
                  <h6 className="dropdown-header text-warning font-weight-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    🎮 <FormattedMessage id="header.nav.games" defaultMessage="Games" />
                  </h6>
                  <a className="dropdown-item text-white" href={`/dino-game${langPrefix}`} onClick={closeMenu}>
                    🦖 <FormattedMessage id="header.nav.dino" defaultMessage="Dino Game" />
                  </a>
                  <a className="dropdown-item text-white" href={`/tic-tac-toe${langPrefix}`} onClick={closeMenu}>
                    ⭕ <FormattedMessage id="header.nav.tictactoe" defaultMessage="Tic Tac Toe" />
                  </a>
                  <a className="dropdown-item text-white font-weight-bold" href={`/math-bingo${langPrefix}`} onClick={closeMenu}>
                    🎯 <FormattedMessage id="header.nav.mathBingo" defaultMessage="Math Bingo" /> <span className="badge badge-warning text-dark ml-1">HOT</span>
                  </a>
                  <a className="dropdown-item text-white font-weight-bold" href={`/math-wizard${langPrefix}`} onClick={closeMenu}>
                    🧙‍♂️ <FormattedMessage id="header.nav.mathWizard" defaultMessage="Math Wizard RPG" /> <span className="badge badge-warning text-dark ml-1">HOT</span>
                  </a>
                  <a className="dropdown-item text-white font-weight-bold" href={`/adventure-runner${langPrefix}`} onClick={closeMenu}>
                    🧭 <FormattedMessage id="header.nav.adventureRunner" defaultMessage="Adventure Runner" /> <span className="badge badge-warning text-dark ml-1">NEW</span>
                  </a>
                  <a className="dropdown-item text-white font-weight-bold" href={`/digital-escape-room${langPrefix}`} onClick={closeMenu}>
                    🔐 <FormattedMessage id="header.nav.escapeRoom" defaultMessage="Digital Escape Room" /> <span className="badge badge-warning text-dark ml-1">NEW</span>
                  </a>
                  <a className="dropdown-item text-white font-weight-bold" href={`/pacman-timer${langPrefix}`} onClick={closeMenu}>
                    🕹️ <FormattedMessage id="header.nav.pacman" defaultMessage="Pac-Man Classroom Timer" />
                  </a>

                  <div className="dropdown-divider border-secondary"></div>
                  <h6 className="dropdown-header text-success font-weight-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    📚 <FormattedMessage id="header.nav.educationHub" defaultMessage="Education & Articles" />
                  </h6>
                  <a className="dropdown-item text-white font-weight-bold" href={activeLang === 'en' ? '/stem-gamification-in-education' : `/gamificacao-na-educacao-stem${langPrefix}`} onClick={closeMenu}>
                    ✨ <FormattedMessage id="header.nav.gamificationPillar" defaultMessage="Gamification in STEM Education" />
                  </a>

                  <div className="dropdown-divider border-secondary"></div>
                  <a className="dropdown-item text-white font-weight-bold" href="/blog" onClick={closeMenu}>
                    📝 <FormattedMessage id="header.nav.blog" defaultMessage="Blog" />
                  </a>
                </div>
              )}
            </li>

            {/* Language Switcher Dropdown */}
            <li className={`nav-item dropdown ${activeDropdown === 'lang' ? 'show' : ''}`}>
              <a
                className="nav-link dropdown-toggle font-weight-bold d-flex align-items-center text-white"
                href="#languages"
                id="languageDropdown"
                role="button"
                onClick={(e) => toggleDropdown('lang', e)}
                aria-haspopup="true"
                aria-expanded={activeDropdown === 'lang'}
              >
                🌐 <span className="text-uppercase ml-1">{activeLang}</span>
              </a>
              {hasInteracted && (
                <div className={`dropdown-menu dropdown-menu-right bg-dark ${activeDropdown === 'lang' ? 'show' : ''}`} aria-labelledby="languageDropdown" style={{ zIndex: 10000000 }}>
                  <h6 className="dropdown-header text-muted font-weight-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    <FormattedMessage id="header.languages" defaultMessage="Languages" />
                  </h6>
                  {SUPPORTED_LANGS.map((lang) => {
                    const isCurrent = lang === activeLang;
                    const localizedUrl = getLocalizedUrl(location.pathname, lang);
                    return (
                      <a
                        key={lang}
                        className={`dropdown-item ${isCurrent ? 'text-warning font-weight-bold' : 'text-white'}`}
                        href={localizedUrl}
                        onClick={closeMenu}
                      >
                        <span className="mr-2">{LANGUAGE_FLAGS[lang] || '🌐'}</span>
                        {LANGUAGE_LABELS[lang] || lang}
                      </a>
                    );
                  })}
                </div>
              )}
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default withRouter(injectIntl(Header));